import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utilities/axios';

// Async thunks
export const fetchLeavePolicies = createAsyncThunk(
  'leavePolicy/fetchLeavePolicies',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/leave-policies');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave policies');
    }
  }
);

export const fetchLeavePolicyByDepartment = createAsyncThunk(
  'leavePolicy/fetchLeavePolicyByDepartment',
  async (departmentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/leave-policies/department/${departmentId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave policy');
    }
  }
);

export const updatePolicySetting = createAsyncThunk(
  'leavePolicy/updatePolicySetting',
  async ({ departmentId, policyName, enabled }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`/leave-policies/department/${departmentId}/setting`, {
        policyName,
        enabled
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update policy setting');
    }
  }
);

export const createLeavePolicy = createAsyncThunk(
  'leavePolicy/createLeavePolicy',
  async ({ departmentId, policies }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/leave-policies', {
        departmentId,
        policies
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create leave policy');
    }
  }
);

export const updateLeavePolicy = createAsyncThunk(
  'leavePolicy/updateLeavePolicy',
  async ({ policyId, policies }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/leave-policies/${policyId}`, {
        policies
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update leave policy');
    }
  }
);

const initialState = {
  policies: [],
  loading: false,
  error: null,
  updating: {},
  currentPolicy: null
};

const leavePolicySlice = createSlice({
  name: 'leavePolicy',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPolicy: (state) => {
      state.currentPolicy = null;
    },
    setUpdating: (state, action) => {
      const { departmentId, isUpdating } = action.payload;
      state.updating = {
        ...state.updating,
        [departmentId]: isUpdating
      };
    }
  },
  extraReducers: (builder) => {
    // Fetch all policies
    builder
      .addCase(fetchLeavePolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeavePolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.policies = action.payload;
      })
      .addCase(fetchLeavePolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch policy by department
    builder
      .addCase(fetchLeavePolicyByDepartment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeavePolicyByDepartment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPolicy = action.payload;
      })
      .addCase(fetchLeavePolicyByDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update policy setting
    builder
      .addCase(updatePolicySetting.pending, (state, action) => {
        const departmentId = action.meta.arg.departmentId;
        state.updating = {
          ...state.updating,
          [departmentId]: true
        };
        state.error = null;
      })
      .addCase(updatePolicySetting.fulfilled, (state, action) => {
        const updatedPolicy = action.payload;
        const departmentId = updatedPolicy.department._id || updatedPolicy.department;
        
        // Update the policy in the policies array
        const existingIndex = state.policies.findIndex(policy => {
          const policyDeptId = typeof policy.department === 'object' ? policy.department._id : policy.department;
          return policyDeptId === departmentId;
        });
        
        if (existingIndex !== -1) {
          state.policies[existingIndex] = updatedPolicy;
        } else {
          state.policies.push(updatedPolicy);
        }
        
        // Also update currentPolicy if it matches
        if (state.currentPolicy) {
          const currentPolicyDeptId = typeof state.currentPolicy.department === 'object' ? state.currentPolicy.department._id : state.currentPolicy.department;
          if (currentPolicyDeptId === departmentId) {
            state.currentPolicy = updatedPolicy;
          }
        }
        
        state.updating = {
          ...state.updating,
          [departmentId]: false
        };
      })
      .addCase(updatePolicySetting.rejected, (state, action) => {
        const departmentId = action.meta.arg.departmentId;
        state.updating = {
          ...state.updating,
          [departmentId]: false
        };
        state.error = action.payload;
      });

    // Create leave policy
    builder
      .addCase(createLeavePolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLeavePolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.policies.push(action.payload);
      })
      .addCase(createLeavePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update leave policy
    builder
      .addCase(updateLeavePolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLeavePolicy.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPolicy = action.payload;
        const index = state.policies.findIndex(
          policy => policy._id === updatedPolicy._id
        );
        if (index !== -1) {
          state.policies[index] = updatedPolicy;
        }
      })
      .addCase(updateLeavePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearCurrentPolicy, setUpdating } = leavePolicySlice.actions;

// Selectors
export const selectLeavePolicies = (state) => state.leavePolicy.policies;
export const selectLeavePolicyLoading = (state) => state.leavePolicy.loading;
export const selectLeavePolicyError = (state) => state.leavePolicy.error;
export const selectLeavePolicyUpdating = (state) => state.leavePolicy.updating;
export const selectCurrentPolicy = (state) => state.leavePolicy.currentPolicy;

// Helper selector to get policy for a specific department
export const selectPolicyByDepartment = (state, departmentId) => {
  return state.leavePolicy.policies.find(
    policy => policy.department._id === departmentId
  );
};

// Helper selector to check if a specific policy is enabled
export const selectPolicyEnabled = (state, departmentId, policyName) => {
  const policy = state.leavePolicy.policies.find(
    policy => policy.department._id === departmentId
  );
  return policy?.policies?.[policyName]?.enabled ?? true; // Default to enabled
};

export default leavePolicySlice.reducer; 